import { Link } from "react-router-dom";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { Wordmark } from "@/components/site/Header";
import { useLang } from "@/i18n/LanguageContext";
import { resetAnalyticsConsent } from "@/lib/analytics";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

export default function Footer() {
    const { t, prefix } = useLang();
    const href = (to) => (to === "/" ? prefix || "/" : `${prefix}${to}`);

    const col1Hrefs = ["/solutions/developpement-international", "/solutions/programmes-appui-formation", "https://export360.fr/", "/solutions/implantation-rh-international"];
    const col2Hrefs = ["/a-propos", "/a-propos", "/analyses", "/contact"];
    const col1Labels = t("footer.col1");
    const col2Labels = t("footer.col2");

    return (
        <footer data-testid="site-footer" className="bg-weyb-ink text-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
                    <div className="md:col-span-5">
                        <Wordmark dark />
                        <p className="mt-6 text-sm leading-relaxed text-white/55 max-w-sm">
                            {t("footer.definition")}
                        </p>
                        <div className="mt-8 flex items-center gap-6 text-sm text-white/55">
                            <span data-testid="footer-france">{t("footer.france")}</span>
                            <span className="h-3 w-px bg-white/15" aria-hidden="true" />
                            <span data-testid="footer-tunisie">{t("footer.tunisia")}</span>
                            <span className="h-3 w-px bg-white/15" aria-hidden="true" />
                            <a
                                href="https://www.linkedin.com/company/weyb/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-linkedin"
                                aria-label="LinkedIn"
                                className="text-white/55 hover:text-weyb-yellow transition-colors"
                            >
                                <Linkedin size={18} />
                            </a>
                            <span className="h-3 w-px bg-white/15" aria-hidden="true" />
                            <a
                                href="mailto:contact@weyb.fr"
                                data-testid="footer-email"
                                className="text-white/55 hover:text-weyb-yellow transition-colors"
                            >
                                contact@weyb.fr
                            </a>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/40 mb-5">
                            {t("footer.col1Title")}
                        </h3>
                        <ul className="space-y-3">
                            {col1Labels.map((label, i) => (
                                <li key={label}>
                                    {col1Hrefs[i].startsWith("http") ? (
                                        <a
                                            href={col1Hrefs[i]}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-white/65 hover:text-weyb-yellow transition-colors"
                                            data-testid={`footer-link-solutions-${i}`}
                                        >
                                            {label}
                                        </a>
                                    ) : (
                                        <Link
                                            to={href(col1Hrefs[i])}
                                            className="text-sm text-white/65 hover:text-weyb-yellow transition-colors"
                                            data-testid={`footer-link-solutions-${i}`}
                                        >
                                            {label}
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-2">
                        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/40 mb-5">
                            {t("footer.col2Title")}
                        </h3>
                        <ul className="space-y-3">
                            {col2Labels.map((label, i) => (
                                <li key={label}>
                                    <Link
                                        to={href(col2Hrefs[i])}
                                        className="text-sm text-white/65 hover:text-weyb-yellow transition-colors"
                                        data-testid={`footer-link-cabinet-${i}`}
                                    >
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-white/40 mb-5">
                            {t("footer.ctaTitle")}
                        </h3>
                        <p className="text-sm text-white/55 leading-relaxed mb-6">
                            {t("footer.ctaText")}
                        </p>
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-5 py-3 rounded-sm text-sm font-semibold transition-colors"
                        >
                            {t("footer.ctaButton")}
                            <ArrowUpRight size={16} />
                        </a>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p className="text-xs text-white/40">
                        © {new Date().getFullYear()} {t("footer.rights")}
                    </p>
                    <div className="flex items-center gap-6 text-xs text-white/40">
                        <Link to={`${prefix}/mentions-legales`} className="hover:text-white/70 transition-colors" data-testid="footer-legal-mentions">
                            {t("footer.legal1")}
                        </Link>
                        <Link to={`${prefix}/politique-de-confidentialite`} className="hover:text-white/70 transition-colors" data-testid="footer-legal-privacy">
                            {t("footer.legal2")}
                        </Link>
                        <button onClick={resetAnalyticsConsent} className="hover:text-white/70 transition-colors" data-testid="footer-cookie-prefs">
                            {t("footer.legal3")}
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
}
